<script setup lang="ts">
import { ref, onMounted, watch } from '#imports';
import { useI18n } from 'vue-i18n';
import { musicService } from '~/services/musicService';

const { t } = useI18n();

// 定义积分历史记录的类型
interface PointsHistoryItem {
  id: number;
  reason: number; // 积分变动原因/类型
  count: number; // 积分数量
  reason_desc: string; // 描述
  created: string; // 创建时间
}

// 使用props和emit代替defineModel
const props = defineProps<{
  visible: boolean
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>();

// 积分历史记录
const pointsHistory = ref<PointsHistoryItem[]>([]);
const isLoading = ref(false);
const hasMore = ref(true);
const currentPage = ref(1);
const pageSize = ref(10);

// 关闭弹窗
const closePopup = () => {
  emit('update:visible', false);
};

// 格式化日期
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

// 获取积分历史记录
const fetchPointsHistory = async (page = 1, append = false) => {
  if (isLoading.value) return;
  
  try {
    isLoading.value = true;
    const response = await musicService.getScoreHistory({
      page,
      size: pageSize.value
    });
    
    if (response && response.data) {
      if (append) {
        pointsHistory.value = [...pointsHistory.value, ...response.data.list];
      } else {
        pointsHistory.value = response.data.list;
      }
      
      // 判断是否还有更多数据
      hasMore.value = response.data.list.length === pageSize.value;
      currentPage.value = page;
    }
  } catch (error) {
    console.error('获取积分历史记录失败:', error);
  } finally {
    isLoading.value = false;
  }
};

// 加载更多
const loadMore = () => {
  if (hasMore.value && !isLoading.value) {
    fetchPointsHistory(currentPage.value + 1, true);
  }
};

// 监听滚动事件，实现无限滚动
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
  
  // 当滚动到距离底部50px时，加载更多数据
  if (scrollBottom < 50 && hasMore.value && !isLoading.value) {
    loadMore();
  }
};

// 监听弹窗显示状态变化
watch(() => props.visible, (newValue: boolean) => {
  if (newValue) {
    // 弹窗显示时，重置状态并获取数据
    currentPage.value = 1;
    hasMore.value = true;
    fetchPointsHistory(1, false);
  }
});

// 阻止点击弹窗内容时关闭弹窗
const stopPropagation = (event: Event) => {
  event.stopPropagation();
};
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" 
         class="fixed inset-0 bg-black/50 px-4 backdrop-blur-sm z-50 flex items-center justify-center"
         @click="closePopup">
      <div class="relative w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden"
           style="max-height: 60vh;"
           @click="stopPropagation">
        <!-- 弹窗头部 -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">
            {{ t('user.pointsHistory') }}
          </h3>
          <button @click="closePopup" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <div class="i-lucide-x text-xl"></div>
          </button>
        </div>
        
        <!-- 弹窗内容 -->
        <div class="px-6 py-4 overflow-y-auto" style="max-height: calc(60vh - 70px);" @scroll="handleScroll">
          <!-- 积分历史记录列表 -->
          <div v-if="pointsHistory.length > 0" class="space-y-4">
            <div v-for="item in pointsHistory" :key="item.id" 
                 class="flex items-center p-3 rounded-lg">
              <div class="flex-1">
                <div class="flex justify-between items-center">
                  <div class="font-medium text-gray-900 dark:text-white truncate">{{ item.reason_desc }}</div>
                  <div :class="[item.reason > 0 ? 'text-green-500' : 'text-red-500', 'font-medium']">
                    {{ item.reason > 0 ? '+' : '-' }}{{ item.count }}
                  </div>
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {{ formatDate(item.created) }}
                </div>
              </div>
            </div>
            
            <!-- 加载更多 -->
            <div v-if="hasMore" class="text-center py-2">
              <div v-if="isLoading" class="inline-block i-lucide-loader-2 animate-spin text-blue-500 text-xl"></div>
              <button v-else @click="loadMore" class="text-blue-500 hover:text-blue-600 dark:hover:text-blue-400">
                {{ t('common.loadMore') }}
              </button>
            </div>
            <div v-else-if="pointsHistory.length > 0" class="text-center py-2 text-gray-500 dark:text-gray-400 text-sm">
              {{ t('common.noMoreData') }}
            </div>
          </div>
          
          <!-- 加载中 -->
          <div v-else-if="isLoading" class="flex justify-center items-center py-8">
            <div class="i-lucide-loader-2 animate-spin text-blue-500 text-2xl"></div>
            <span class="ml-2 text-gray-600 dark:text-gray-300">{{ t('common.loading') }}</span>
          </div>
          
          <!-- 无数据 -->
          <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
            {{ t('user.noPointsHistory') }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template> 